import type { TypedClient } from '@platform/openapi';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createUploader,
  pollUntil,
  putWithProgress,
  UploadFailedError,
  waitFor,
} from '../media/upload-file.ts';
import { usePageContext } from '../usePageContext.ts';
import { isAllowed, type Permissions } from './column-choices.ts';
import {
  readPreview,
  suggestedMapping,
  type ImportLocale,
  type ImportView,
  type MappedColumn,
  type Preview,
} from './import-model.ts';

const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 400;
const CSV_TYPE = 'text/csv';
const UNREADABLE = 'unreadable';

export type ImportStep = 'file' | 'mapping' | 'checking' | 'report' | 'applying' | 'done';

export type ImportState = {
  readonly step: ImportStep;
  readonly file: File | null;
  readonly preview: Preview | null;
  readonly columns: readonly MappedColumn[];
  readonly locationId: string | null;
  readonly view: ImportView | null;
  readonly error: string | null;
};

const INITIAL: ImportState = {
  step: 'file',
  file: null,
  preview: null,
  columns: [],
  locationId: null,
  view: null,
  error: null,
};

const asCsv = (file: File): File =>
  file.type === CSV_TYPE ? file : new File([file], file.name, { type: CSV_TYPE });

export const isCsvFile = (file: File): boolean =>
  file.type === CSV_TYPE || file.name.toLocaleLowerCase().endsWith('.csv');

export const useProductImport = (api: TypedClient, defaultLocale: ImportLocale, permissions: Permissions) => {
  const { t, describeError, invalidate } = usePageContext();
  const [state, setState] = useState<ImportState>(INITIAL);
  const aborter = useRef(new AbortController());
  const upload = useMemo(
    () =>
      createUploader({
        api,
        put: putWithProgress,
        wait: waitFor,
        intervalMs: POLL_INTERVAL_MS,
        maxPolls: MAX_POLLS,
      }),
    [api],
  );

  useEffect(() => {
    const controller = new AbortController();
    aborter.current = controller;
    return () => {
      controller.abort();
    };
  }, []);

  const patch = (change: Partial<ImportState>) => {
    if (!aborter.current.signal.aborted) setState((current) => ({ ...current, ...change }));
  };

  const reasonOf = (error: unknown): string => {
    if (error instanceof UploadFailedError)
      return error.reason === UNREADABLE ? t('productImport.unreadable') : t(`errors.${error.reason}`);
    return describeError(error) ?? t('errors.UNKNOWN');
  };

  const settle = (id: string, done: (view: ImportView) => boolean) =>
    pollUntil(
      () => api.get('/admin/imports/{id}', { path: { id } }),
      done,
      { wait: waitFor, intervalMs: POLL_INTERVAL_MS, maxPolls: MAX_POLLS, signal: aborter.current.signal },
      'MEDIA_PROCESSING_FAILED',
    );

  const run = (step: ImportStep, work: () => Promise<Partial<ImportState>>, fallback: ImportStep) => {
    patch({ step, error: null });
    work()
      .then(patch)
      .catch((error: unknown) => {
        patch({ step: fallback, error: reasonOf(error) });
      });
  };

  return {
    state,
    reset: () => {
      setState(INITIAL);
    },
    chooseFile: (file: File) => {
      if (!isCsvFile(file)) {
        patch({ error: t('productImport.notCsv', { name: file.name }) });
        return;
      }
      run(
        'file',
        async () => {
          const preview = await readPreview(file).catch((error: unknown) => {
            throw new UploadFailedError(UNREADABLE, { cause: error });
          });
          return {
            step: 'mapping',
            file,
            preview,
            columns: suggestedMapping(preview.headers, defaultLocale).filter((column) =>
              isAllowed(column.target, permissions),
            ),
          };
        },
        'file',
      );
    },
    setColumns: (columns: readonly MappedColumn[]) => {
      patch({ columns });
    },
    setLocation: (locationId: string | null) => {
      patch({ locationId });
    },
    check: () => {
      const { file, columns, locationId } = state;
      if (file === null) return;
      run(
        'checking',
        async () => {
          const media = await upload(
            asCsv(file),
            'product_import',
            { onPhase: () => undefined, onProgress: () => undefined },
            aborter.current.signal,
          );
          const started = await api.post('/admin/imports', {
            body: { mediaId: media.id, mapping: { columns: [...columns] }, locationId },
          });
          return { step: 'report', view: await settle(started.id, (view) => view.status !== 'validating') };
        },
        'mapping',
      );
    },
    apply: () => {
      const { view } = state;
      if (view === null) return;
      run(
        'applying',
        async () => {
          await api.post('/admin/imports/{id}/status', {
            path: { id: view.id },
            body: { status: 'applying' },
          });
          const finished = await settle(
            view.id,
            (candidate) => candidate.status === 'completed' || candidate.status === 'failed',
          );
          void invalidate('products', 'stock-levels', 'low-stock', 'options', 'dashboard');
          return { step: 'done', view: finished };
        },
        'report',
      );
    },
  };
};
